import { useQueryClient } from "@tanstack/react-query";
import type React from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { PaywallDialog } from "@/components/PaywallDialog";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { loadSessionData, saveEntityData } from "@/utils/database";
import {
  archiveSession,
  createNewUserSession,
  findDuplicateCuiSession,
  generateSessionDisplayName,
  getSessionProgress,
  getSessionStatusText,
  getUserSessions,
  type SessionWithEntity,
} from "@/utils/multi-session";
import { CuiArchivedError } from "@/utils/supabaseFunctionsError";
import { useCreateSessionForGuest } from "../hooks/api/mutate/useCreateSessionForGuest";
import { useDeleteSessionPermanently } from "../hooks/api/mutate/useDeleteSessionPermanently";
import {
  clearUnauthenticatedEntityData,
  getUnauthenticatedEntityData,
  type UnauthenticatedEntityData,
} from "../utils/localStorageEntityData";

export const CURRENT_SESSION_KEY = "current_session_id";

interface SessionSwitcherContextType {
  // Session list management
  sessions: SessionWithEntity[];
  archivedSessions: SessionWithEntity[];
  isLoadingSessions: boolean;
  refreshSessions: () => Promise<void>;

  // Current session
  currentSessionId: string | null;
  currentSession: SessionWithEntity | null;
  isLoadingSessionSwitch: boolean;

  // Session operations
  createSession: () => Promise<boolean>;
  switchToSession: (sessionId: string) => Promise<boolean>;
  deleteSessionById: (sessionId: string) => Promise<void>;
  archiveSessionById: (sessionId: string) => Promise<void>;
  refreshCurrentSession: () => Promise<void>;

  // Session display helpers
  getSessionDisplayName: (session: SessionWithEntity, index: number) => string;
  getSessionProgress: (session: SessionWithEntity) => number;
  getSessionStatusText: (session: SessionWithEntity) => string;

  // Session data for wizard
  sessionData: any;

  // Paywall management
  showPaywall: boolean;
  setShowPaywall: (show: boolean) => void;
}

const SessionSwitcherContext = createContext<
  SessionSwitcherContextType | undefined
>(undefined);

export function SessionSwitcherProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [sessions, setSessions] = useState<SessionWithEntity[]>([]);
  const [archivedSessions, setArchivedSessions] = useState<SessionWithEntity[]>(
    []
  );
  const [isLoadingSessions, setIsLoadingSessions] = useState(true);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [currentSession, setCurrentSession] =
    useState<SessionWithEntity | null>(null);
  const [isLoadingSessionSwitch, setIsLoadingSessionSwitch] = useState(false);
  const [sessionData, setSessionData] = useState<any>(null);
  const [showPaywall, setShowPaywall] = useState(false);

  const { toast } = useToast();
  const { user } = useAuth();

  const { mutateAsync: createSessionForGuestAsync } =
    useCreateSessionForGuest();
  const { mutateAsync: deleteSessionPermanentlyAsync } =
    useDeleteSessionPermanently();

  const isCreatingSessionRef = useRef(false);
  const currentSessionIdRef = useRef(currentSessionId);

  useEffect(() => {
    currentSessionIdRef.current = currentSessionId;
  }, [currentSessionId]);

  // Initialize current session from localStorage
  useEffect(() => {
    const savedSessionId = localStorage.getItem(CURRENT_SESSION_KEY);
    if (savedSessionId) {
      setCurrentSessionId(savedSessionId);
    }
  }, []);

  // Listen for auth state changes and clear session only on explicit logout
  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_OUT") {
        // Only clear localStorage on explicit logout
        localStorage.removeItem(CURRENT_SESSION_KEY);
        setCurrentSessionId(null);
        setCurrentSession(null);
        setSessionData(null);
        setSessions([]);
        setArchivedSessions([]);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Load sessions when user changes or component mounts
  const refreshSessions = useCallback(async () => {
    if (!user) {
      setSessions([]);
      setArchivedSessions([]);
      setIsLoadingSessions(false);
      return;
    }

    try {
      setIsLoadingSessions(true);
      const allSessions = await getUserSessions({ includeArchived: true });
      const activeSessions = allSessions.filter((s) => !s.archived_at);
      const archived = allSessions.filter((s) => !!s.archived_at);

      const unauthenticatedEntityData: UnauthenticatedEntityData | null =
        getUnauthenticatedEntityData();
      let newSession = null;

      if (
        (activeSessions.length === 0 || unauthenticatedEntityData) &&
        !isCreatingSessionRef.current
      ) {
        isCreatingSessionRef.current = true;
        try {
          if (unauthenticatedEntityData) {
            const cui = unauthenticatedEntityData.entityData.cui;
            const match = findDuplicateCuiSession({
              cui,
              sessions: activeSessions,
              archivedSessions: archived,
            });

            if (match?.isArchived) {
              // Reject the guest migration: archived sessions must be
              // unarchived explicitly from Companiile mele before use.
              clearUnauthenticatedEntityData();
              toast({
                title: "Companie arhivată",
                description:
                  "Această companie este arhivată. Dezarhiveaz-o din Companiile mele pentru a continua.",
                variant: "destructive",
              });
              // Create a blank session so the user isn't stranded with
              // zero active sessions and no way forward.
              newSession = await createNewUserSession();
              if (newSession) activeSessions.push(newSession);
            } else if (match) {
              clearUnauthenticatedEntityData();
              setCurrentSessionId(match.session.id);
              localStorage.setItem(CURRENT_SESSION_KEY, match.session.id);
              toast({
                title: "CUI existent",
                description:
                  "Acest CUI există deja în contul tău. Am selectat sesiunea existentă.",
              });
            } else {
              const isFromLandingPage =
                unauthenticatedEntityData.currentStep === 0;
              const result = await createSessionForGuestAsync({
                isFromLandingPage,
                cui,
              });
              newSession = result.session;

              if (result.insufficient_credits) {
                setShowPaywall(true);
                toast({
                  title: "Credite insuficiente",
                  description:
                    "Pentru a continua de la pasul salvat, aveți nevoie de credite suplimentare.",
                  variant: "destructive",
                });
              }

              await saveEntityData(
                newSession.id,
                unauthenticatedEntityData.entityData
              );
              clearUnauthenticatedEntityData();
              const sessionWithEntity: SessionWithEntity = {
                ...newSession,
                entity_data: unauthenticatedEntityData.entityData,
              };
              newSession = sessionWithEntity;
              activeSessions.push(sessionWithEntity);
            }
          } else {
            newSession = await createNewUserSession();
            if (newSession) activeSessions.push(newSession);
          }
        } catch (err) {
          // The server-side backstop may reject create-for-guest with
          // CuiArchivedError if it detects an archived match we missed.
          if (err instanceof CuiArchivedError) {
            clearUnauthenticatedEntityData();
            toast({
              title: "Companie arhivată",
              description:
                "Această companie este arhivată. Dezarhiveaz-o din Companiile mele pentru a continua.",
              variant: "destructive",
            });
          } else {
            throw err;
          }
        } finally {
          isCreatingSessionRef.current = false;
        }
      }

      setSessions(activeSessions);
      setArchivedSessions(archived);

      if (!currentSessionIdRef.current && activeSessions.length > 0) {
        const sessionId = newSession?.id ?? activeSessions[0].id;
        setCurrentSessionId(sessionId);
        localStorage.setItem(CURRENT_SESSION_KEY, sessionId);
      }
    } catch (error) {
      console.error("Error loading sessions:", error);
      toast({
        title: "Eroare",
        description: "Nu s-au putut încărca sesiunile.",
        variant: "destructive",
      });
    } finally {
      setIsLoadingSessions(false);
    }
  }, [user, toast, createSessionForGuestAsync]);

  useEffect(() => {
    refreshSessions();
  }, [refreshSessions]);

  // Update current session object when sessionId or sessions change
  useEffect(() => {
    if (currentSessionId && sessions.length > 0) {
      const session = sessions.find((s) => s.id === currentSessionId);
      setCurrentSession(session || null);
    } else {
      setCurrentSession(null);
    }
  }, [currentSessionId, sessions]);

  // Load session data when current session changes
  useEffect(() => {
    if (currentSessionId) {
      loadCurrentSessionData(currentSessionId);
    }
  }, [currentSessionId]);

  const loadCurrentSessionData = async (sessionId: string) => {
    try {
      const data = await loadSessionData(sessionId);
      if (data) {
        setSessionData(data);
      } else {
        // Initialize with default test data
        setSessionData({
          entityData: {},
          selectedServices: [],
          entitySize: "MARE",
        });
      }
    } catch (error) {
      console.error("Error loading session data:", error);
      setSessionData({
        entityData: {},
        selectedServices: [],
        entitySize: "MARE",
      });
    }
  };

  const createSession = async (): Promise<boolean> => {
    if (!user) {
      toast({
        title: "Eroare",
        description: "Trebuie să fii autentificat pentru a crea o sesiune.",
        variant: "destructive",
      });
      return false;
    }

    try {
      const newSession = await createNewUserSession();
      if (newSession) {
        // Refresh sessions to include the new one
        await refreshSessions();

        // Switch to the new session
        await switchToSession(newSession.id);

        toast({
          title: "Succes",
          description: "Compania a fost creată cu succes.",
        });
        return true;
      } else {
        toast({
          title: "Eroare",
          description: "Nu s-a putut crea compania nouă.",
          variant: "destructive",
        });
        return false;
      }
    } catch (error) {
      console.error("Error creating session:", error);
      toast({
        title: "Eroare",
        description: "A apărut o eroare la crearea companiei noi.",
        variant: "destructive",
      });
      return false;
    }
  };

  const switchToSession = async (sessionId: string): Promise<boolean> => {
    if (sessionId === currentSessionId) {
      return true; // Already on this session
    }

    const previousSessionId = currentSessionId;

    try {
      setIsLoadingSessionSwitch(true);

      // Optimistically update the session
      setCurrentSessionId(sessionId);
      localStorage.setItem(CURRENT_SESSION_KEY, sessionId);

      // Load session data
      await loadCurrentSessionData(sessionId);

      toast({
        title: "Sesiune schimbată",
        description: "Compania a fost încărcată cu succes.",
      });

      return true;
    } catch (error) {
      console.error("Error switching session:", error);

      // Revert to previous session on error
      if (previousSessionId) {
        setCurrentSessionId(previousSessionId);
        localStorage.setItem(CURRENT_SESSION_KEY, previousSessionId);
        await loadCurrentSessionData(previousSessionId);
      }

      toast({
        title: "Eroare",
        description:
          "Nu s-a putut încărca compania. S-a revenit la compania anterioară.",
        variant: "destructive",
      });

      return false;
    } finally {
      setIsLoadingSessionSwitch(false);
    }
  };

  const archiveSessionById = async (sessionId: string) => {
    try {
      const success = await archiveSession(sessionId);
      if (success) {
        // If archiving the active session, auto-switch to the most-recently-updated
        // remaining active session (sessions list is sorted by updated_at desc),
        // or create a new one if none remain.
        if (sessionId === currentSessionId) {
          const remainingSessions = sessions.filter((s) => s.id !== sessionId);
          if (remainingSessions.length > 0) {
            await switchToSession(remainingSessions[0].id);
          } else {
            await createSession();
          }
        }

        await refreshSessions();

        toast({
          title: "Succes",
          description: "Compania a fost arhivată.",
        });
      } else {
        toast({
          title: "Eroare",
          description: "Nu s-a putut arhiva compania.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error archiving session:", error);
      toast({
        title: "Eroare",
        description: "A apărut o eroare la arhivarea companiei.",
        variant: "destructive",
      });
    }
  };

  const deleteSessionById = async (sessionId: string) => {
    try {
      await deleteSessionPermanentlyAsync(sessionId);

      // If we're deleting the current session, switch to another one —
      // same fallback path as archiveSessionById.
      if (sessionId === currentSessionId) {
        const remainingSessions = sessions.filter((s) => s.id !== sessionId);
        if (remainingSessions.length > 0) {
          await switchToSession(remainingSessions[0].id);
        } else {
          await createSession();
        }
      }

      await refreshSessions();

      toast({
        title: "Succes",
        description: "Compania a fost ștearsă cu succes.",
      });
    } catch (error) {
      console.error("Error deleting session:", error);
      toast({
        title: "Eroare",
        description: "A apărut o eroare la ștergerea companiei.",
        variant: "destructive",
      });
    }
  };

  const refreshCurrentSession = async () => {
    if (!currentSessionId || !user) return;

    try {
      const allSessions = await getUserSessions({ includeArchived: true });
      const active = allSessions.filter((s) => !s.archived_at);
      const archived = allSessions.filter((s) => !!s.archived_at);

      setSessions(active);
      setArchivedSessions(archived);

      const updatedCurrentSession = allSessions.find(
        (s) => s.id === currentSessionId
      );
      if (updatedCurrentSession) {
        setCurrentSession(updatedCurrentSession);
      }
    } catch (error) {
      console.error("Error refreshing current session:", error);
    }
  };

  const value: SessionSwitcherContextType = {
    // Session list management
    sessions,
    archivedSessions,
    isLoadingSessions,
    refreshSessions,

    // Current session
    currentSessionId,
    currentSession,
    isLoadingSessionSwitch,

    // Session operations
    createSession,
    switchToSession,
    deleteSessionById,
    archiveSessionById,
    refreshCurrentSession,

    // Session display helpers
    getSessionDisplayName: generateSessionDisplayName,
    getSessionProgress,
    getSessionStatusText,

    // Session data
    sessionData,

    // Paywall management
    showPaywall,
    setShowPaywall,
  };

  return (
    <SessionSwitcherContext.Provider value={value}>
      {children}
      <PaywallDialog open={showPaywall} onOpenChange={setShowPaywall} />
    </SessionSwitcherContext.Provider>
  );
}

export function useSessionSwitcher() {
  const context = useContext(SessionSwitcherContext);
  if (context === undefined) {
    throw new Error(
      "useSessionSwitcher must be used within a SessionSwitcherProvider"
    );
  }
  return context;
}
